import { CreatePaymentIntentDto, e2eTestQueryKeys, cardInfoQueryKeys } from '@sweatpals/shared';
import axios from 'axios';

export const handleStripeMockCardPayment = async (
	paymentIntent: CreatePaymentIntentDto,
	cardInfo: {
		CARD_NUMBER?: string | number | null;
		CARD_EXP_MONTH?: string | number | null;
		CARD_EXP_YEAR?: string | number | null;
		CARD_CVC?: string | number | null;
	}
): Promise<boolean> => {
	// check for Stripe test environment
	if (!paymentIntent?.intent || !paymentIntent.intent.publishableKey.includes('pk_test_')) {
		return false;
	}

	if (!cardInfo.CARD_NUMBER || !cardInfo.CARD_EXP_MONTH || !cardInfo.CARD_EXP_YEAR || !cardInfo.CARD_CVC) {
		return false;
	}

	const CARD_NUMBER = String(cardInfo.CARD_NUMBER);
	const CARD_EXP_MONTH = String(cardInfo.CARD_EXP_MONTH);
	const CARD_EXP_YEAR = String(cardInfo.CARD_EXP_YEAR);
	const CARD_CVC = String(cardInfo.CARD_CVC);

	const params = new URLSearchParams();
	params.append('payment_method_data[type]', 'card');
	params.append('payment_method_data[card][number]', CARD_NUMBER);
	params.append('payment_method_data[card][exp_month]', CARD_EXP_MONTH);
	params.append('payment_method_data[card][exp_year]', CARD_EXP_YEAR);
	params.append('payment_method_data[card][cvc]', CARD_CVC);
	params.append('key', paymentIntent.intent.publishableKey);
	params.append('client_secret', paymentIntent.intent.paymentIntent);

	const response = await axios.post(
		`https://api.stripe.com/v1/payment_intents/${paymentIntent.intent.paymentIntentId}/confirm`,
		params,
		{
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				Authorization: `Bearer ${paymentIntent.intent.publishableKey}`,
			},
		}
	);

	if (response.data.status === 'succeeded') {
		return true;
	}

	return false;
};

export const handleE2ECardPayment = async (props: {
	createPaymentIntent(e2eToken?: string | null): Promise<CreatePaymentIntentDto>,
	onSubmit?(paymentIntent: CreatePaymentIntentDto): Promise<void>,
	onMockSubmit?(paymentIntent: CreatePaymentIntentDto): Promise<void>
}) => {
	const { createPaymentIntent, onSubmit, onMockSubmit } = props;

	const queryParams = new URLSearchParams(window.location.search);

	const isE2EMode = !!queryParams.get(e2eTestQueryKeys.isE2E);

	// if not E2E mode then no token is needed
	const e2eToken = isE2EMode ? queryParams.get(e2eTestQueryKeys.e2eToken) : undefined;

	// create payment intent
	const paymentIntent = await createPaymentIntent(e2eToken);

	// if not in E2E mode then call onSubmit
	if (!isE2EMode) {
		await onSubmit?.(paymentIntent);

		return;
	}

	// if price is 0 then call onMockSubmit
	if (paymentIntent.fullPrice <= 0) {
		await onMockSubmit?.(paymentIntent);

		return;
	}

	// if isE2EMode then mock the stripe payment request
	const success = await handleStripeMockCardPayment(paymentIntent, {
		CARD_NUMBER: queryParams.get(cardInfoQueryKeys.CARD_NUMBER),
		CARD_EXP_MONTH: queryParams.get(cardInfoQueryKeys.CARD_EXP_MONTH),
		CARD_EXP_YEAR: queryParams.get(cardInfoQueryKeys.CARD_EXP_YEAR),
		CARD_CVC: queryParams.get(cardInfoQueryKeys.CARD_CVC),
	});

	// if payment is successful then call onMockSubmit
	if (success) {
		await onMockSubmit?.(paymentIntent);
	}
};
